/**
 * Google Analytics 4 (gtag.js), consent-gated (POPIA).
 *
 * Rules this file enforces in one place:
 *  - Nothing loads unless the backend reports a GA_MEASUREMENT_ID AND the
 *    visitor pressed Accept. gtag.js is injected from here, never from
 *    index.html (so the inline-script CSP hash is unaffected).
 *  - Google Consent Mode v2: everything defaults to denied; only
 *    analytics_storage is ever granted. Ad signals/personalisation stay off.
 *  - Only allow-listed event and param names are sent, and param values are
 *    short enum-like strings or numbers. Unknown names are dropped.
 *  - Paths are sanitised (no query, no ids/tokens); debtor-facing and
 *    token routes are never tracked (EXCLUDED_PREFIXES).
 *  - No user_id, no emails, names, phone numbers, org names or invoice data.
 */

export const CONSENT_KEY = 'rl_analytics_consent'
export type ConsentChoice = 'granted' | 'denied'

/**
 * Hard exclusion list: customer-facing/debtor routes and anything carrying a
 * secret in the URL. Matched on the first path segment(s). Several of these
 * are served by the backend, not the SPA; they are listed anyway so a future
 * SPA route can never be tracked by accident.
 */
export const EXCLUDED_PREFIXES = [
  '/i',
  '/c',
  '/api',
  '/consent',
  '/whatsapp',
  '/unsubscribe',
  '/stop',
  '/email-stop',
  '/opt-out',
  '/opt-outs',
  '/optout',
  '/mandate',
  '/reset',
  '/forgot',
  '/forgot-password',
  '/join',
  '/admin',
  '/docs',
  '/health',
] as const

export function isExcludedPath(pathname: string): boolean {
  const p = (pathname.split(/[?#]/)[0] || '/').toLowerCase()
  return EXCLUDED_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/'))
}

// Path segments that follow these prefixes are always identifiers.
const ID_AFTER: Array<[string, string]> = [
  ['/customers/', ':id'],
  ['/practice/clients/', ':id'],
]
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const LONG_HEX = /^[0-9a-f]{16,}$/i
const TOKENISH = /^[A-Za-z0-9_-]{24,}$/
const DIGITS = /^\d+$/

/** Pathname only; ids/tokens become :id; never a query string or hash. */
export function sanitizePath(pathname: string): string {
  let p = pathname.split(/[?#]/)[0] || '/'
  if (!p.startsWith('/')) p = '/' + p
  const lower = p.toLowerCase()
  for (const [prefix, placeholder] of ID_AFTER) {
    if (lower.startsWith(prefix)) {
      const segs = p.split('/')
      const idx = prefix.split('/').length - 1
      if (segs[idx]) segs[idx] = placeholder
      p = segs.join('/')
      break
    }
  }
  const out = p
    .split('/')
    .map((s) => (UUID.test(s) || LONG_HEX.test(s) || TOKENISH.test(s) || DIGITS.test(s) ? ':id' : s))
    .join('/')
  return out.length > 100 ? out.slice(0, 100) : out
}

// Typed allow-list: event name -> permitted param names.
const EVENT_PARAMS = {
  page_view: ['page_path', 'page_title'],
  sign_up_started: ['plan', 'signup_source'],
  sign_up_completed: ['plan', 'signup_source'],
  login: [],
  import_started: [],
  import_completed: ['count'],
  start_sending: [],
  pause_customer: [],
  setup_checklist_step: ['step'],
  cta_click: ['label'],
  pricing_plan_selected: ['plan'],
  waitlist_joined: ['source'],
  faq_opened: ['question_index'],
  scroll_depth: ['depth'],
  billing_checkout_started: ['plan'],
  billing_completed: ['plan'],
  connection_started: ['provider'],
  connection_connected: ['provider'],
  rule_saved: [],
  message_edited: [],
  manual_send: ['channel'],
  invoice_added: [],
  invoices_closed: ['count_bucket'],
  reminders_exported: [],
  send_paused: [],
  send_resumed: [],
  alert_settings_saved: [],
  help_opened: [],
  feedback_sent: [],
  ui_error: ['error_kind', 'page_path'],
  search_used: [],
} as const satisfies Record<string, readonly string[]>

export type AnalyticsEventName = keyof typeof EVENT_PARAMS
type ParamsOf<E extends AnalyticsEventName> = {
  [K in (typeof EVENT_PARAMS)[E][number]]?: string | number
}

/** Params whose value must be one of a closed set (anything else is dropped). */
export const ENUM_PARAMS: Record<string, readonly (string | number)[]> = {
  plan: ['starter', 'business', 'scale', 'enterprise'],
  provider: ['xero', 'quickbooks', 'sage', 'csv'],
  channel: ['whatsapp', 'sms'],
  period: ['monthly', 'annual'],
  error_kind: ['network', 'server', 'validation', 'unknown'],
  count_bucket: ['1', '2-5', '6-20', '21+'],
  depth: [25, 50, 75, 100],
}

const SAFE_VALUE = /^[A-Za-z0-9_:/ .-]{1,100}$/
const SIGNUP_SOURCE = /^[a-z0-9_.-]{1,40}$/

/** Pure: returns the params that may be sent, or null when the event is unknown. */
export function filterEvent(name: string, params: Record<string, unknown> = {}): Record<string, string | number> | null {
  if (!Object.prototype.hasOwnProperty.call(EVENT_PARAMS, name)) return null
  const allowed = EVENT_PARAMS[name as AnalyticsEventName] as readonly string[]
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(params)) {
    if (!allowed.includes(k)) continue
    const enumValues = ENUM_PARAMS[k]
    if (enumValues) {
      if ((typeof v === 'string' || typeof v === 'number') && enumValues.includes(v)) out[k] = v
    } else if (k === 'signup_source') {
      if (typeof v === 'string' && SIGNUP_SOURCE.test(v)) out[k] = v
    } else if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
    else if (typeof v === 'string' && SAFE_VALUE.test(v)) out[k] = v
  }
  return out
}

// ---- buckets ---------------------------------------------------------------

/** 1 | 2-5 | 6-20 | 21+ ; null for zero/invalid so the event is not sent. */
export function countBucket(n: number): string | null {
  if (!Number.isFinite(n) || n < 1) return null
  if (n === 1) return '1'
  if (n <= 5) return '2-5'
  if (n <= 20) return '6-20'
  return '21+'
}

export function accountAgeBucket(createdAt: string | null | undefined, now: Date = new Date()): string | null {
  if (!createdAt) return null
  const t = Date.parse(createdAt)
  if (Number.isNaN(t)) return null
  const days = Math.max(0, Math.floor((now.getTime() - t) / 86_400_000))
  if (days <= 7) return '0-7d'
  if (days <= 30) return '8-30d'
  if (days <= 90) return '31-90d'
  return '90d+'
}

// ---- acquisition (first-touch UTM) ----------------------------------------

export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const UTM_VALUE = /^[A-Za-z0-9_.+ -]{1,60}$/
const ATTRIBUTION_KEY = 'rl_attribution'

/** Keeps ONLY utm_* keys with short safe values; everything else is dropped. */
export function sanitizeUtm(search: string): Partial<Record<(typeof UTM_KEYS)[number], string>> {
  const out: Partial<Record<(typeof UTM_KEYS)[number], string>> = {}
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return out
  }
  for (const k of UTM_KEYS) {
    const v = params.get(k)
    if (v && UTM_VALUE.test(v)) out[k] = v.trim()
  }
  return out
}

export function utmQuery(utm: Partial<Record<string, string>>): string {
  const q = new URLSearchParams()
  for (const k of UTM_KEYS) if (utm[k]) q.set(k, utm[k]!)
  const s = q.toString()
  return s ? '?' + s : ''
}

interface Attribution {
  utm: Partial<Record<(typeof UTM_KEYS)[number], string>>
  ref: string
}

/** Host only (no path/query) of an external referrer; '' for same-site/none. */
export function referrerHost(referrer: string, ownHost: string): string {
  try {
    if (!referrer) return ''
    const h = new URL(referrer).hostname.toLowerCase()
    return h && h !== ownHost.toLowerCase() ? h.slice(0, 100) : ''
  } catch {
    return ''
  }
}

export function getAttribution(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY)
    return raw ? (JSON.parse(raw) as Attribution) : null
  } catch {
    return null
  }
}

/** First-touch only: call on first landing. Nothing is sent from here. */
export function captureAttribution(): void {
  try {
    if (getAttribution() || isExcludedPath(window.location.pathname)) return
    const a: Attribution = {
      utm: sanitizeUtm(window.location.search),
      ref: referrerHost(document.referrer, window.location.hostname),
    }
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(a))
  } catch {
    // storage unavailable: no attribution
  }
}

/** utm_source, else 'direct'. Safe to pass as the signup_source param. */
export function signupSource(): string {
  const src = getAttribution()?.utm.utm_source?.toLowerCase().replace(/\s+/g, '_')
  return src && SIGNUP_SOURCE.test(src) ? src : 'direct'
}

// ---- state -----------------------------------------------------------------

type Gtag = (...args: unknown[]) => void
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

let measurementId = ''
let configLoaded = false
let loaded = false
const listeners = new Set<() => void>()
let version = 0
let bannerRequested = false

function emit() {
  version += 1
  listeners.forEach((l) => l())
}
export function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}
export function getVersion(): number {
  return version
}

export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

export function isConfigured(): boolean {
  return measurementId !== ''
}
export function isBannerVisible(): boolean {
  return isConfigured() && (getConsent() === null || bannerRequested)
}

/** Fetch the public config once. Failure means "no analytics". */
export async function initAnalytics(): Promise<void> {
  if (configLoaded) return
  configLoaded = true
  try {
    const r = await fetch('/api/v1/public-config')
    if (r.ok) {
      const body = (await r.json()) as { ga_measurement_id?: unknown }
      const id = typeof body.ga_measurement_id === 'string' ? body.ga_measurement_id : ''
      measurementId = /^G-[A-Z0-9]{4,20}$/.test(id) ? id : ''
    }
  } catch {
    measurementId = ''
  }
  if (measurementId && getConsent() === 'granted') {
    enableGtag()
    // The route-change timer may have fired before the config arrived.
    trackPageView()
  }
  emit()
}

function pageLocation(path: string): string {
  return window.location.origin + path
}

function enableGtag() {
  if (!measurementId || typeof document === 'undefined') return
  const w = window as unknown as Record<string, unknown>
  w[`ga-disable-${measurementId}`] = false
  if (loaded) {
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' })
    return
  }
  loaded = true
  window.dataLayer = window.dataLayer || []
  // gtag must push the `arguments` object itself, not an array.
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  window.gtag('consent', 'update', { analytics_storage: 'granted' })
  window.gtag('js', new Date())
  // Campaign attribution: only the allow-listed utm_* keys and the external
  // referrer's host are ever passed; the rest of the query string is dropped.
  const attr = getAttribution()
  const utm = attr?.utm ?? {}
  window.gtag('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: pageLocation(sanitizePath(window.location.pathname)) + utmQuery(utm),
    page_referrer: attr?.ref ? `https://${attr.ref}/` : '',
    ...(utm.utm_source && { campaign_source: utm.utm_source }),
    ...(utm.utm_medium && { campaign_medium: utm.utm_medium }),
    ...(utm.utm_campaign && { campaign_name: utm.utm_campaign }),
    ...(utm.utm_content && { campaign_content: utm.utm_content }),
    ...(utm.utm_term && { campaign_term: utm.utm_term }),
  })
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(s)
}

function deleteGaCookies() {
  try {
    for (const c of document.cookie.split(';')) {
      const name = c.split('=')[0].trim()
      if (name === '_ga' || name.startsWith('_ga_') || name === '_gid') {
        document.cookie = `${name}=; Max-Age=0; path=/`
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`
      }
    }
  } catch {
    // ignore
  }
}

export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice)
  } catch {
    // storage unavailable: choice applies for this page view only
  }
  bannerRequested = false
  if (choice === 'granted') {
    enableGtag()
    trackPageView()
  } else if (loaded && measurementId) {
    ;(window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = true
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' })
    deleteGaCookies()
  }
  emit()
}

/** "Cookie settings" link: show the banner again. */
export function reopenBanner(): void {
  bannerRequested = true
  emit()
}

function canSend(): boolean {
  return (
    measurementId !== '' &&
    getConsent() === 'granted' &&
    loaded &&
    typeof window.gtag === 'function' &&
    !isExcludedPath(window.location.pathname)
  )
}

export function track<E extends AnalyticsEventName>(name: E, params?: ParamsOf<E>): void {
  try {
    if (!canSend()) return
    const clean = filterEvent(name, params as Record<string, unknown>)
    if (!clean) return
    window.gtag!('event', name, { ...clean, page_location: pageLocation(sanitizePath(window.location.pathname)) })
  } catch {
    // analytics must never break the app
  }
}

// Marketing pages keep their real <title>; app pages may put a customer's
// name in the title, so they report the sanitised path instead.
const MARKETING_PATHS = ['/', '/features', '/integrations', '/pricing', '/about', '/contact', '/terms', '/privacy', '/operator-agreement']

let lastPath = ''
export function trackPageView(): void {
  try {
    const path = sanitizePath(window.location.pathname)
    // Leaving through an excluded route must let the same page count again.
    if (isExcludedPath(window.location.pathname)) lastPath = ''
    if (!canSend()) return
    if (path === lastPath) return
    lastPath = path
    const title = MARKETING_PATHS.includes(path) ? document.title.slice(0, 100) : path
    track('page_view', { page_path: path, page_title: title })
  } catch {
    // ignore
  }
}

/**
 * User properties: enums/buckets only, never ids, names or org details.
 * Set once consented and logged in; refreshed whenever the inputs change.
 */
const USER_PROP_ENUMS: Record<string, readonly string[]> = {
  plan_tier: ['starter', 'business', 'scale', 'enterprise', 'trial'],
  role: ['owner', 'manager', 'member'],
  account_age_bucket: ['0-7d', '8-30d', '31-90d', '90d+'],
  setup_complete: ['true', 'false'],
  billing_status: ['trial', 'pending_setup', 'active', 'past_due', 'cancelled'],
}

export function buildUserProperties(
  user: { org_role?: string; sending_started?: boolean } | null,
  org: { subscription_tier?: string; billing_status?: string; created_at?: string } | null,
  now: Date = new Date(),
): Record<string, string> {
  const out: Record<string, string> = {}
  if (!user) return out
  const role = user.org_role === 'owner' || user.org_role === undefined ? 'owner' : user.org_role === 'manager' ? 'manager' : 'member'
  out.role = role
  out.setup_complete = user.sending_started === true ? 'true' : 'false'
  if (org) {
    out.plan_tier = org.billing_status === 'trial' ? 'trial' : (org.subscription_tier ?? '')
    out.billing_status = org.billing_status ?? ''
    const age = accountAgeBucket(org.created_at, now)
    if (age) out.account_age_bucket = age
  }
  for (const k of Object.keys(out)) {
    if (!USER_PROP_ENUMS[k]?.includes(out[k])) delete out[k]
  }
  return out
}

export function setUserProperties(props: Record<string, string>): void {
  try {
    if (measurementId === '' || getConsent() !== 'granted' || !loaded || typeof window.gtag !== 'function') return
    const clean: Record<string, string> = {}
    for (const [k, v] of Object.entries(props)) if (USER_PROP_ENUMS[k]?.includes(v)) clean[k] = v
    if (Object.keys(clean).length) window.gtag('set', 'user_properties', clean)
  } catch {
    // ignore
  }
}

const CHECKOUT_PLAN_KEY = 'rl_checkout_plan'
/** Remember the plan across the Paystack round trip (same tab only). */
export function rememberCheckoutPlan(plan: string | undefined): void {
  try {
    if (plan && ENUM_PARAMS.plan.includes(plan)) sessionStorage.setItem(CHECKOUT_PLAN_KEY, plan)
  } catch {
    // ignore
  }
}
export function takeCheckoutPlan(): string | undefined {
  try {
    const p = sessionStorage.getItem(CHECKOUT_PLAN_KEY) ?? undefined
    sessionStorage.removeItem(CHECKOUT_PLAN_KEY)
    return p
  } catch {
    return undefined
  }
}

let uiErrors = 0
/** Error class only, never the message. Capped per page load. */
export function trackUiError(kind: 'network' | 'server' | 'validation' | 'unknown'): void {
  if (uiErrors >= 5) return
  uiErrors += 1
  track('ui_error', { error_kind: kind, page_path: sanitizePath(window.location.pathname) })
}

/** Test hook: forget module state. */
export function _resetForTests(): void {
  measurementId = ''
  configLoaded = false
  loaded = false
  lastPath = ''
  bannerRequested = false
  uiErrors = 0
}

const PLAN_KEYS = ['starter', 'business', 'scale']
/** Marketing call-to-action click; `plan` only if it is a known plan key. */
export function trackCta(label: string, plan?: string): void {
  track('cta_click', { label })
  if (plan && PLAN_KEYS.includes(plan)) track('pricing_plan_selected', { plan })
}
